import {gql} from '@apollo/client';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getSharedSmartChipProps, SharedSmartChipProps} from 'ui-library/chips/ChipUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {BasicEvent} from 'util/Types';

import {
  AnalysisTemplateChipPopoverQuery,
  AnalysisTemplateChipPopoverQueryVariables,
} from './__generated/AnalysisTemplateChipPopoverQuery';
import ChipPopoverContent from './ChipPopoverContent';
import {NO_ACCESS_CHIP_TEXT} from './NoAccessChip';
import QueryingChipPopoverContent from './QueryingChipPopoverContent';
import SmartChip from './SmartChip';
import useSmartChipQuery, {SmartChipType} from './useSmartChipQuery';

export type PopoverAnalysisTemplate = AnalysisTemplateChipPopoverQuery['analysisTemplate'];

export const ANALYSIS_TEMPLATE_CHIP_POPOVER_QUERY = gql`
  query AnalysisTemplateChipPopoverQuery($analysisTemplateId: StrictString!)
  @skipAudit
  @engComponent(name: ANALYSIS_TOOL) {
    analysisTemplate(id: $analysisTemplateId) {
      ...AnalysisTemplateChipOrInaccessibleGraphQL
    }
  }
  ${getFragmentWrapper('AnalysisTemplateChipOrInaccessible').fragment}
`;

function AnalysisTemplateChipPopoverContent({
  analysisTemplate,
}: {
  analysisTemplate: PopoverAnalysisTemplate;
}): JSX.Element | null {
  if (isInaccessible(analysisTemplate)) {
    return <>{NO_ACCESS_CHIP_TEXT}</>;
  }
  const fields = [
    {
      label: 'Name',
      value: (
        <>
          <BenchlingIcon icon={ModelTypeConstants.ANALYSIS_TEMPLATE} /> &nbsp; {analysisTemplate.name}
        </>
      ),
    },
    {label: 'ID', value: <>{analysisTemplate.id}</>},
  ];
  return <ChipPopoverContent info={{fields}} />;
}

export function QueryingAnalysisTemplateChipPopoverContent({id}: {id: string}): JSX.Element {
  return (
    <QueryingChipPopoverContent<AnalysisTemplateChipPopoverQuery, AnalysisTemplateChipPopoverQueryVariables>
      variables={{analysisTemplateId: id}}
      query={ANALYSIS_TEMPLATE_CHIP_POPOVER_QUERY}
    >
      {(data: AnalysisTemplateChipPopoverQuery) => (
        <AnalysisTemplateChipPopoverContent analysisTemplate={data.analysisTemplate} />
      )}
    </QueryingChipPopoverContent>
  );
}

export interface AnalysisTemplateChipProps extends SharedSmartChipProps<SmartChipType.ANALYSIS_TEMPLATE> {}

export default function AnalysisTemplateChip(props: AnalysisTemplateChipProps): JSX.Element {
  const sharedSmartChipProps = getSharedSmartChipProps(props);
  const {id, onClick} = sharedSmartChipProps;

  const {
    item: analysisTemplate,
    handleClickLink,
    isInaccessible,
    itemUrl: analysisTemplateUrl,
  } = useSmartChipQuery<SmartChipType.ANALYSIS_TEMPLATE>({
    id,
    onClick,
    getUrlFn: (analysisTemplate) => analysisTemplate.editUrl,
  });
  const onClickHandler = (e: BasicEvent): void => {
    if (analysisTemplate) {
      if (onClick != null) {
        handleClickLink(e);
      } else {
        FileBrowserActions.openFile(analysisTemplate.id);
      }
    }
  };
  return (
    <SmartChip
      {...sharedSmartChipProps}
      icon={ModelTypeConstants.ANALYSIS_TEMPLATE}
      isInaccessible={isInaccessible}
      item={analysisTemplate}
      onClick={onClickHandler}
      url={analysisTemplateUrl}
      popoverContentOverride={id ? <QueryingAnalysisTemplateChipPopoverContent id={id} /> : null}
    />
  );
}
